import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../utils/config';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  History,
  Loader2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  ExternalLink,
  ChevronDown,
} from 'lucide-react';

const PayoutHistoryCard = () => {
  const { token } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/referrals/rewards/payout-history?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPayouts(data.payouts || []);
        setTotal(data.total || 0);
      } else {
        throw new Error('Failed to load payout history');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(cents / 100);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'succeeded':
        return (
          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
            <CheckCircle2 className="w-3 h-3 mr-1" /> Completed
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive">
            <XCircle className="w-3 h-3 mr-1" /> Failed
          </Badge>
        );
      case 'created':
        return (
          <Badge variant="outline">
            <Clock className="w-3 h-3 mr-1" /> Processing
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="text-center py-4">
          <p className="text-muted-foreground text-sm mb-2">{error}</p>
          <Button variant="ghost" size="sm" onClick={fetchHistory}>
            <RefreshCw className="w-4 h-4 mr-2" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  if (payouts.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-center gap-3 mb-4">
          <History className="w-5 h-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">Payout History</h3>
        </div>
        <p className="text-sm text-muted-foreground text-center py-4">
          No payouts yet. Keep sharing your referral link to earn rewards!
        </p>
      </div>
    );
  }

  const displayedPayouts = expanded ? payouts : payouts.slice(0, 3);

  return (
    <div className="bg-card rounded-xl border border-border p-6" data-testid="payout-history-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <History className="w-5 h-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">Payout History</h3>
          {total > 0 && (
            <Badge variant="secondary" className="text-xs">{total} total</Badge>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={fetchHistory}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <div className="space-y-3">
        {displayedPayouts.map((payout) => (
          <div
            key={payout.id}
            className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <p className="font-medium text-foreground">
                  {formatCurrency(payout.amount_cents)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(payout.created_at)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {getStatusBadge(payout.status)}
              {payout.stripe_object_id && (
                <a
                  href={`https://dashboard.stripe.com/transfers/${payout.stripe_object_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {payouts.length > 3 && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full mt-4"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Show Less' : `Show ${payouts.length - 3} More`}
          <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </Button>
      )}
    </div>
  );
};

export default PayoutHistoryCard;
